import { RoleSelectionDialog } from "components/dialog/RoleSelectionDialog";
import type { CdcUserAttributes } from "models/cdcUser";
import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { getDataFromLocalstorageByKey } from "service/utils/get-data-localstorage";
import { isPredefinedRole } from "utils/predefined-roles.utils";
import { useFeatureFlags } from "../hooks/use-config";
import {
  type OnboardingGateState,
  useOnboardingGate,
} from "../hooks/use-onboarding-gate";
import { type UserInfoState, useUserInfo } from "../hooks/use-user-info";

export type UserInfoProviderState = UserInfoState & OnboardingGateState;

const UserInfoContext = createContext<UserInfoProviderState | null>(null);

export const UserInfoProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const flags = useFeatureFlags();
  const [cdcUser, setCdcUser] = useState<CdcUserAttributes | undefined>(() =>
    getDataFromLocalstorageByKey("cdcUser"),
  );
  const userInfoState = useUserInfo(!!cdcUser);
  const { gateState, retry } = useOnboardingGate({
    enabled: !!cdcUser,
    email: cdcUser?.email,
    userInfoStatus: userInfoState.status,
    refreshUserInfo: userInfoState.refresh,
  });

  const currentRole: string | undefined = cdcUser?.extData?.work?.cov_job_title;
  const needsRoleSelection =
    flags.onboardingV2 &&
    !!cdcUser &&
    gateState === "ready" &&
    !isPredefinedRole(currentRole);

  // The dialog persists the new role to localStorage; re-read so the dialog
  // dismisses on the same render.
  const handleRoleSaved = useCallback(() => {
    setCdcUser(getDataFromLocalstorageByKey("cdcUser"));
  }, []);

  return (
    <UserInfoContext.Provider value={{ ...userInfoState, gateState, retry }}>
      {children}
      <RoleSelectionDialog
        open={needsRoleSelection}
        previousRole={currentRole}
        onSaved={handleRoleSaved}
      />
    </UserInfoContext.Provider>
  );
};

export function useUserInfoContext(): UserInfoProviderState {
  const context = useContext(UserInfoContext);
  if (!context) {
    throw new Error(
      "useUserInfoContext must be used within a UserInfoProvider",
    );
  }
  return context;
}
